import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { siPrestashop, siShopify, siWordpress } from "simple-icons";
import {
  ArrowRight,
  Bot,
  ChevronDown,
  Inbox,
  Code2,
  Copy,
  Download,
  ExternalLink,
  Eye,
  Globe,
  Layers,
  Lock,
  MessageSquare,
  Plus,
  Settings,
  Sparkles,
  Store,
} from "lucide-react";
import {
  useAllSources,
  useBot,
  useContacts,
  useContactStats,
  useConversations,
  useConversationStats,
  useIntegrations,
  useSources,
  useWidgetEmbed,
} from "../hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AppTile,
  BrandCard,
  BrandStyles,
  FONDO_CODICE,
  HandNote,
  TESTO_AZIONE,
  TESTO_COPIATO,
  VARIABILI_SEZIONE,
  coloreApp,
  marchio,
  tintSurface,
} from "../components/integrations/brand";
import { BOTTONE, GRIGIO, SCURO, IntestazioneCasella } from "../components/integrations/casella";
import { APPS } from "../components/integrations/registry";

// Il plugin WordPress ufficiale e' quello di WordPress.org («PingUp AI+», slug
// pingupai): lo zip e' sempre l'ultima versione, e chi lo installa riceve gli
// aggiornamenti da WordPress. La pagina del plugin, per chi lo cerca da li'.
const PLUGIN_WORDPRESS_ZIP = "https://downloads.wordpress.org/plugin/pingupai.latest-stable.zip";
const PLUGIN_WORDPRESS_PAGINA = "https://wordpress.org/plugins/pingupai/";

// Quanti contatti e conversazioni recenti mostrare.
const RECENTI = 4;

const STATI_BOT = {
  active: { chiave: "statusActive", testo: "Attivo", pallino: "bg-[#19D163]", fondo: "bg-[#19D163]/10 text-[#0F7A3D]" },
  processing: { chiave: "statusProcessing", testo: "In elaborazione", pallino: "bg-amber-500", fondo: "bg-amber-50 text-amber-800" },
  error: { chiave: "statusError", testo: "Errore", pallino: "bg-red-500", fondo: "bg-red-50 text-red-700" },
};

/**
 * La Panoramica di un bot: come sta, cosa e' successo di recente e come
 * metterlo sul sito. Stessa identita' di Apps & Integrazioni, Contatti e
 * Conversazioni: intestazione di una riga, card bianche, pulsanti grigi.
 * I numeri portano alla loro pagina; gli ultimi contatti e le ultime
 * conversazioni si aprono gia' selezionati.
 */
export default function BotDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: bot, isLoading, error } = useBot(id);
  const { data: sourcesResponse, isLoading: sourcesLoading } = useSources(id);
  const { data: allSources } = useAllSources(id);

  if (isLoading || sourcesLoading) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-4 pt-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-[14px]" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-[14px]" />
      </div>
    );
  }

  if (error || !bot) {
    return (
      <BrandCard className="mx-auto mt-6 max-w-lg p-6 text-center">
        <Bot className="mx-auto h-8 w-8 text-[#94A3B8]" aria-hidden="true" />
        <h1 className="mt-3 text-base font-semibold text-[#020617]">
          {error ? t("common.error") : t("botDetail.notFound")}
        </h1>
        <p className="mt-1 text-sm text-[#64748B]">{error ? error.message : t("botDetail.notFoundDesc")}</p>
      </BrandCard>
    );
  }

  // Le fonti comprendono le pagine delle sitemap (per questo /all).
  const fonti = Array.isArray(allSources)
    ? allSources.length
    : sourcesResponse?.pagination?.total_items ?? (sourcesResponse?.sources?.length || 0);

  return <Panoramica bot={bot} botId={id} fonti={fonti} />;
}

function Panoramica({ bot, botId, fonti }) {
  const { t } = useTranslation();
  const stato = STATI_BOT[bot.status];

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={Bot}
        titolo={bot.name}
        dettagli={
          <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            {stato ? (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${stato.fondo}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${stato.pallino}`} aria-hidden="true" />
                {t(`botDetail.overview.${stato.chiave}`, stato.testo)}
              </span>
            ) : null}
            {bot.description ? <span className="line-clamp-1 max-w-xl text-sm text-[#64748B]">{bot.description}</span> : null}
          </span>
        }
        azioni={
          <Link to={`/bots/${botId}/settings`} className={GRIGIO}>
            <Settings className="h-3.5 w-3.5" aria-hidden="true" />
            {t("common.settings", "Impostazioni")}
          </Link>
        }
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-6 pb-10">
        {fonti > 0 ? (
          <>
            <Numeri bot={bot} botId={botId} fonti={fonti} />
            {/* Alti uguali: con un riquadro vuoto l'altro non resta da solo. */}
            <div className="grid items-stretch gap-4 lg:grid-cols-2">
              <UltimiContatti botId={botId} />
              <UltimeConversazioni botId={botId} />
            </div>
            <Installazione bot={bot} botId={botId} />
          </>
        ) : (
          <PrimaFonte botId={botId} />
        )}
      </div>
    </div>
  );
}

/** Nessuna fonte ancora: senza, il bot non sa cosa rispondere. */
function PrimaFonte({ botId }) {
  const { t } = useTranslation();
  return (
    <BrandCard className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div className="max-w-lg space-y-2">
        <h2 className="text-lg font-semibold text-[#020617]">{t("botDetail.getStarted")}</h2>
        <p className="text-sm leading-6 text-[#475569]">{t("botDetail.getStartedDesc")}</p>
        <Link to={`/bots/${botId}/sources`} className={`${SCURO} mt-3`}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t("botDetail.addFirstSource")}
        </Link>
      </div>
      <HandNote>{t("botDetail.overview.startHere", "si parte da qui")}</HandNote>
    </BrandCard>
  );
}

// ---------------------------------------------------------------------------
// I numeri: ognuno porta alla sua pagina
// ---------------------------------------------------------------------------

function Numeri({ bot, botId, fonti }) {
  const { t, i18n } = useTranslation();
  const { data: conv } = useConversationStats(botId);
  const { data: contatti } = useContactStats(botId);
  const { data: integrazioni } = useIntegrations(botId);
  const attive = integrazioni ? APPS.filter((app) => integrazioni[app.settingsKey]?.enabled) : null;

  const aggiornate = bot.last_ingest_at
    ? t("botDetail.overview.sourcesUpdated", "Aggiornate il {{when}}", {
        when: new Date(bot.last_ingest_at).toLocaleDateString(i18n.language, { day: "numeric", month: "long" }),
      })
    : t("botDetail.stats.knowledgeItems");

  const voci = [
    {
      chiave: "fonti",
      etichetta: t("botDetail.stats.sources"),
      valore: fonti,
      nota: aggiornate,
      a: `/bots/${botId}/sources`,
    },
    {
      chiave: "conversazioni",
      etichetta: t("botDetail.stats.conversations"),
      valore: conv?.total_conversations,
      // Come i contatti: se ce ne sono da leggere, lo dice per prime.
      nota: conv
        ? conv.inbox?.new
          ? t("botDetail.overview.conversationsToRead", "{{count}} da leggere", { count: conv.inbox.new })
          : t("botDetail.overview.questions", "{{count}} domande dei visitatori", { count: conv.total_messages || 0 })
        : null,
      a: `/bots/${botId}/conversations`,
      evidenza: Boolean(conv?.inbox?.new),
    },
    {
      chiave: "contatti",
      etichetta: t("botDetail.overview.contactsToRead", "Contatti da leggere"),
      valore: contatti?.new,
      nota: contatti ? t("botDetail.overview.contactsTotal", "{{count}} in tutto", { count: contatti.total || 0 }) : null,
      a: `/bots/${botId}/contatti`,
      evidenza: Boolean(contatti?.new),
    },
    {
      chiave: "app",
      etichetta: t("botDetail.overview.activeApps", "App attive"),
      valore: attive?.length,
      nota: attive
        ? attive.length
          ? attive.map((app) => app.title).join(", ")
          : t("botDetail.overview.noApps", "Nessuna, per ora")
        : null,
      a: `/bots/${botId}/integrazioni`,
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {voci.map((voce) => (
        <Link
          key={voce.chiave}
          to={voce.a}
          className="group rounded-[14px] border border-[#E2E8F0] bg-white p-5 transition-colors hover:border-[#CBD5E1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex items-center justify-between gap-2 text-xs font-medium text-[#64748B]">
            <span className="inline-flex items-center gap-1.5">
              {voce.evidenza ? <span className="h-1.5 w-1.5 rounded-full bg-[#A855F7]" aria-hidden="true" /> : null}
              {voce.etichetta}
            </span>
            <ArrowRight
              className="h-3.5 w-3.5 -translate-x-1 text-[#94A3B8] opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100"
              aria-hidden="true"
            />
          </span>
          <span className="mt-2 block text-[28px] font-semibold leading-9 tabular-nums tracking-tight text-[#020617]">
            {typeof voce.valore === "number" ? voce.valore : "—"}
          </span>
          <span className="mt-1 block truncate text-xs text-[#64748B]">{voce.nota || " "}</span>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cosa e' successo di recente
// ---------------------------------------------------------------------------

function Riquadro({ titolo, tutti, a, children }) {
  return (
    <BrandCard className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 pb-2 pt-4">
        <h2 className="text-sm font-semibold text-[#020617]">{titolo}</h2>
        <Link to={a} className="inline-flex items-center gap-1 text-xs font-medium text-[#64748B] hover:text-[#020617]">
          {tutti}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
      <div className="flex flex-1 flex-col px-2 pb-2">{children}</div>
    </BrandCard>
  );
}

function Riga({ a, iniziali, titolo, testo, quando, nuovo }) {
  return (
    <Link
      to={a}
      className="flex items-start gap-3 rounded-[10px] px-3 py-2.5 transition-colors hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <span className="relative shrink-0">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F5F9] text-[11px] font-semibold text-[#334155]"
          aria-hidden="true"
        >
          {iniziali}
        </span>
        {nuovo ? (
          <span className="absolute -left-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#A855F7] ring-2 ring-white" aria-hidden="true" />
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className={`truncate text-sm ${nuovo ? "font-semibold text-[#020617]" : "font-medium text-[#334155]"}`}>{titolo}</span>
          <span className="shrink-0 text-xs tabular-nums text-[#94A3B8]">{quando}</span>
        </span>
        {testo ? <span className="mt-0.5 block truncate text-[13px] text-[#64748B]">{testo}</span> : null}
      </span>
    </Link>
  );
}

function useQuando() {
  const { i18n } = useTranslation();
  return (valore) => {
    const data = new Date(valore);
    const oggi = new Date().toDateString() === data.toDateString();
    return oggi
      ? data.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })
      : data.toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
  };
}

const inizialiDi = (testo) =>
  String(testo || "")
    .split(/[\s@.]+/)
    .filter((p) => /\p{L}/u.test(p))
    .slice(0, 2)
    .map((p) => p.match(/\p{L}/u)[0].toUpperCase())
    .join("") || "?";

/** Riquadro vuoto: cosa arrivera' qui e il pulsante per cominciare. */
function Vuoto({ Icona, titolo, testo, azione }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1F5F9]">
        <Icona className="h-5 w-5 text-[#94A3B8]" aria-hidden="true" />
      </span>
      <div className="max-w-xs space-y-1">
        <p className="text-sm font-medium text-[#020617]">{titolo}</p>
        <p className="text-xs leading-5 text-[#64748B]">{testo}</p>
      </div>
      {azione}
    </div>
  );
}

function UltimiContatti({ botId }) {
  const { t } = useTranslation();
  const { data: integrazioni } = useIntegrations(botId);
  const moduloAcceso = Boolean(integrazioni?.contact_form?.enabled);
  const quando = useQuando();
  const { data, isLoading } = useContacts(botId, { page: 1, limit: RECENTI, status: "open" });
  const contatti = data?.contacts || [];
  return (
    <Riquadro
      titolo={t("botDetail.overview.latestContacts", "Ultimi contatti")}
      tutti={t("botDetail.overview.seeAllContacts", "Vedi tutti")}
      a={`/bots/${botId}/contatti`}
    >
      {isLoading ? (
        <Skeleton className="m-3 h-24" />
      ) : contatti.length ? (
        contatti.map((c) => (
          <Riga
            key={c.id}
            a={`/bots/${botId}/contatti?contatto=${encodeURIComponent(c.id)}`}
            iniziali={inizialiDi(c.headline)}
            titolo={c.headline}
            testo={c.context?.summary || c.context?.topic}
            quando={quando(c.created_at)}
            nuovo={c.status === "new"}
          />
        ))
      ) : (
        <Vuoto
          Icona={Inbox}
          titolo={t("botDetail.overview.noContactsYet", "Ancora nessun contatto.")}
          testo={
            moduloAcceso
              ? t("botDetail.overview.noContactsFormOn", "Il modulo è attivo: quando qualcuno lo compila in chat, lo trovi qui.")
              : t("botDetail.overview.noContactsFormOff", "Crea il modulo contatti: l'assistente lo propone in chat e le richieste arrivano qui.")
          }
          azione={
            <Link to={`/bots/${botId}/integrazioni/modulo-contatti`} className={moduloAcceso ? GRIGIO : SCURO}>
              {moduloAcceso
                ? t("botDetail.overview.seeForm", "Vedi il modulo")
                : t("botDetail.overview.createForm", "Crea il modulo contatti")}
            </Link>
          }
        />
      )}
    </Riquadro>
  );
}

function UltimeConversazioni({ botId }) {
  const { t } = useTranslation();
  const quando = useQuando();
  const { data, isLoading } = useConversations(botId, { page: 1, limit: RECENTI });
  const conversazioni = data?.conversations || [];
  return (
    <Riquadro
      titolo={t("botDetail.overview.latestConversations", "Ultime conversazioni")}
      tutti={t("botDetail.overview.seeAllConversations", "Vedi tutte")}
      a={`/bots/${botId}/conversations`}
    >
      {isLoading ? (
        <Skeleton className="m-3 h-24" />
      ) : conversazioni.length ? (
        conversazioni.map((c) => {
          const chi = c.contact?.headline || c.metadata?.user_context?.name || t("botDetail.overview.visitor", "Visitatore");
          return (
            <Riga
              key={c.session_id}
              a={`/bots/${botId}/conversations?sessione=${encodeURIComponent(c.session_id)}`}
              iniziali={
                c.contact || c.metadata?.user_context?.name ? (
                  inizialiDi(chi)
                ) : (
                  <MessageSquare className="h-3.5 w-3.5 text-[#94A3B8]" />
                )
              }
              titolo={String(c.first_question || c.preview || "").replace(/\s*\{\{[^}]*\}\}/g, "")}
              testo={chi}
              quando={quando(c.last_activity)}
            />
          );
        })
      ) : (
        <Vuoto
          Icona={MessageSquare}
          titolo={t("botDetail.overview.noConversationsYet", "Ancora nessuna conversazione.")}
          testo={t("botDetail.overview.noConversationsHint", "Le chat dei visitatori arrivano qui. Prova l'assistente dall'anteprima del widget.")}
          azione={
            <Link to={`/bots/${botId}/widget`} className={GRIGIO}>
              {t("botDetail.overview.tryWidget", "Prova il widget")}
            </Link>
          }
        />
      )}
    </Riquadro>
  );
}

// ---------------------------------------------------------------------------
// Metti il bot sul sito
// ---------------------------------------------------------------------------

/**
 * Dove si puo' mettere il bot: le schede sono quelle di Apps & Integrazioni
 * (alone del colore del marchio, logo ufficiale, pulsante largo quanto la
 * scheda). Le piattaforme non ancora pronte restano, con «In arrivo» e il
 * pulsante spento. Otto schede: quattro per riga, sempre uguali.
 */
const CANALI = [
  { id: "wordpress", nome: "WordPress", desc: "channelWordpressDesc", brand: marchio(siWordpress), pronto: true },
  { id: "manual", desc: "channelManualDesc", icon: Code2, tint: "slate", pronto: true },
  { id: "shopify", nome: "Shopify", desc: "channelShopifyDesc", brand: marchio(siShopify) },
  { id: "prestashop", nome: "PrestaShop", desc: "channelPrestashopDesc", brand: marchio(siPrestashop) },
  // Senza logo in simple-icons: l'icona sul colore del marchio.
  { id: "magento", nome: "Magento", desc: "channelMagentoDesc", icon: Store, brand: { hex: "#EE672F", title: "Magento" } },
  { id: "extendify", nome: "Extendify", desc: "channelExtendifyDesc", icon: Layers, tint: "violet" },
  { id: "shoptet", nome: "Shoptet", desc: "channelShoptetDesc", icon: Store, tint: "orange" },
  { id: "macaly", nome: "Macaly", desc: "channelMacalyDesc", icon: Sparkles, brand: { hex: "#0F172A", title: "Macaly" } },
];

// Quante schede per riga: le stesse classi della griglia (1 / sm 2 / lg 4).
function useColonne() {
  const calcola = () => {
    if (typeof window.matchMedia !== "function") return 4;
    if (window.matchMedia("(min-width: 1024px)").matches) return 4;
    if (window.matchMedia("(min-width: 640px)").matches) return 2;
    return 1;
  };
  const [colonne, setColonne] = useState(calcola);
  useEffect(() => {
    const aggiorna = () => setColonne(calcola());
    window.addEventListener("resize", aggiorna);
    return () => window.removeEventListener("resize", aggiorna);
  }, []);
  return colonne;
}

function Installazione({ bot, botId }) {
  const { t } = useTranslation();
  const [scelta, setScelta] = useState(null);
  const nomeDi = (canale) => canale.nome || t("botDetail.widgetIntegration.channelManual");
  const colonne = useColonne();
  const pannello = useRef(null);

  // Il pannello si apre subito sotto la riga della scheda premuta (non in
  // fondo alla griglia, fuori dallo schermo) e la pagina ci scorre sopra:
  // chi clicca vede sempre cosa e' successo.
  const indice = CANALI.findIndex((c) => c.id === scelta);
  const fineRiga = indice < 0 ? -1 : Math.min(Math.floor(indice / colonne) * colonne + colonne - 1, CANALI.length - 1);
  useEffect(() => {
    if (scelta) pannello.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [scelta]);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-[#020617]">
          {t("botDetail.overview.installTitle", "Metti l'Assistente AI sul tuo sito")}
        </h2>
        <p className="mt-1 text-sm text-[#64748B]">{t("botDetail.widgetIntegration.channelsIntro")}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CANALI.map((canale, i) => (
          <React.Fragment key={canale.id}>
            <div className="pu-carta-in grid" style={{ "--ritardo": `${Math.min(i, 8) * 12}ms` }}>
              <SchedaCanale
                canale={canale}
                nome={nomeDi(canale)}
                descrizione={t(`botDetail.widgetIntegration.${canale.desc}`)}
                aperta={scelta === canale.id}
                onApri={() => setScelta(scelta === canale.id ? null : canale.id)}
              />
            </div>
            {i === fineRiga ? (
              <div ref={pannello} className="pu-carta-in col-span-full scroll-mt-4" role="region" aria-label={nomeDi(CANALI[indice])}>
                <BrandCard className="relative p-5 sm:p-6">
                  {scelta === "wordpress" ? <PannelloWordpress bot={bot} /> : <PannelloCodice botId={botId} bot={bot} />}
                </BrandCard>
              </div>
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

function SchedaCanale({ canale, nome, descrizione, aperta, onApri }) {
  const { t } = useTranslation();
  const app = { ...canale, title: nome };
  return (
    <BrandCard
      className={`group relative flex flex-col p-4 transition-[border-color,box-shadow] ${
        aperta ? "border-[#18181B] shadow-[0_0_0_1px_#18181B,0_5px_5px_rgba(0,0,0,0.06)]" : "hover:border-[#CBD5E1] hover:shadow-[0_5px_5px_rgba(0,0,0,0.06)]"
      }`}
      style={tintSurface(coloreApp(app))}
    >
      <div className="flex items-start justify-between gap-2">
        <AppTile app={app} />
        {canale.pronto ? null : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-medium text-[#92400E] shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
            {t("botDetail.overview.comingSoonBadge", "In arrivo")}
          </span>
        )}
      </div>
      {canale.pronto ? (
        // Tutta la scheda apre la configurazione (il titolo e' un pulsante esteso).
        <button
          type="button"
          onClick={onApri}
          aria-expanded={aperta}
          className="mt-4 text-left text-[15px] font-semibold text-[#020617] outline-none after:absolute after:inset-0 after:rounded-[14px] focus-visible:after:ring-2 focus-visible:after:ring-ring"
        >
          {nome}
        </button>
      ) : (
        <p className="mt-4 text-[15px] font-semibold text-[#020617]">{nome}</p>
      )}
      <p className="mt-1 line-clamp-2 flex-1 text-xs leading-[18px] text-[#475569]">{descrizione}</p>

      <div className="mt-4">
        {canale.pronto ? (
          <button
            type="button"
            onClick={onApri}
            className="relative z-10 inline-flex h-9 w-full items-center justify-center gap-1 rounded-[10px] bg-[#F1F5F9] px-3 text-xs font-semibold text-[#18181B] transition-colors hover:bg-[#E2E8F0]"
          >
            {aperta ? t("botDetail.overview.close", "Chiudi") : t("botDetail.overview.setUp", "Configura")}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${aperta ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        ) : (
          // Stesso pulsante delle altre schede, spento: la scheda resta uguale.
          <button
            type="button"
            disabled
            title={t("botDetail.overview.comingSoonBadge", "In arrivo")}
            className="inline-flex h-9 w-full cursor-not-allowed items-center justify-center gap-1 rounded-[10px] bg-[#F8FAFC] px-3 text-xs font-semibold text-[#94A3B8]"
          >
            {t("botDetail.overview.setUp", "Configura")}
          </button>
        )}
      </div>
    </BrandCard>
  );
}

function PannelloWordpress({ bot }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl">
          <h3 className="text-sm font-semibold text-[#020617]">{t("botDetail.widgetIntegration.wordpressTitle")}</h3>
          <p className="mt-1 text-sm text-[#64748B]">{t("botDetail.widgetIntegration.wordpressIntro")}</p>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <a href={PLUGIN_WORDPRESS_ZIP} className={SCURO}>
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            {t("botDetail.widgetIntegration.wordpressDownload")}
          </a>
          <a
            href={PLUGIN_WORDPRESS_PAGINA}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold ${TESTO_AZIONE}`}
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            {t("botDetail.widgetIntegration.wordpressStorePage")}
          </a>
        </div>
      </div>

      <ol className="space-y-3">
        {[1, 2, 3, 4].map((passo) => (
          <li key={passo} className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-xs font-semibold text-[#020617]">
              {passo}
            </span>
            <div className="pt-0.5">
              <p className="text-sm font-medium text-[#020617]">{t(`botDetail.widgetIntegration.wordpressStep${passo}Title`)}</p>
              <p className="text-sm text-[#64748B]">
                {passo === 4
                  ? t("botDetail.widgetIntegration.wordpressStep4Desc", { botName: bot.name })
                  : t(`botDetail.widgetIntegration.wordpressStep${passo}Desc`)}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <p className="flex items-start gap-2 rounded-[10px] bg-[#F1F5F9] p-3 text-xs leading-5 text-[#475569]">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t("botDetail.widgetIntegration.wordpressSecurityNote")}
      </p>
    </div>
  );
}

// Colori leggeri per il codice: commenti, tag, attributi, valori.
function evidenzia(codice) {
  const sicuro = codice.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const conCommenti = sicuro.replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="text-[#94A3B8] italic">$1</span>');
  const html = conCommenti.replace(/(&lt;\/?)([a-zA-Z][\w:-]*)([^&]*?)(&gt;)/g, (_m, apre, nome, attributi, chiude) => {
    const attr = attributi.replace(
      /(\s)([a-zA-Z_:][\w:.-]*)(\s*=\s*)("[^"]*"|'[^']*')/g,
      (_mm, spazio, nomeAttr, uguale, valore) =>
        `${spazio}<span class="text-[#0F7A3D]">${nomeAttr}</span><span class="text-[#94A3B8]">${uguale}</span><span class="text-[#B91C1C]">${valore}</span>`
    );
    return `${apre}<span class="text-[#2563EB]">${nome}</span>${attr}${chiude}`;
  });
  return { __html: html };
}

function PannelloCodice({ botId, bot }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: embed, isLoading, refetch } = useWidgetEmbed(botId);
  // Cosa e' appena stato copiato: "snippet" o "codice" (per il verde sul bottone).
  const [copiato, setCopiato] = useState(null);

  const codice = () => {
    if (embed?.embed_code) return `<!-- PingUp Chat Widget - Dynamic Configuration -->\n${embed.embed_code}`;
    const widget = embed?.widget_code || bot?.widget_code || "";
    return `<!-- PingUp Chat Widget -->
<script src="${window.location.origin}/widget.build.js" defer></script>
<script>
  (function () {
    window.addEventListener('DOMContentLoaded', function () {
      window.PingUpChatWidgetV2.init("${widget}");
    });
  })(window);
</script>`;
  };

  const copia = async (cosa) => {
    try {
      await navigator.clipboard.writeText(cosa === "codice" ? embed.widget_code : codice());
      setCopiato(cosa);
      setTimeout(() => setCopiato(null), 1500);
      toast({
        variant: "success",
        title: cosa === "codice" ? t("widget.basic.widgetCodeCopied") : t("botDetail.embedCopied"),
      });
    } catch (e) {
      console.error("Codice non copiato", e);
    }
  };
  const testoCopia = (cosa) => (copiato === cosa ? TESTO_COPIATO : TESTO_AZIONE);

  if (isLoading) return <Skeleton className="h-40 w-full rounded-[10px]" />;
  if (!embed) {
    return (
      <div className="space-y-3 text-sm text-[#64748B]">
        <p>{t("botDetail.widgetIntegration.unableToLoad")}</p>
        <button type="button" className={GRIGIO} onClick={() => refetch()}>
          {t("botDetail.retry", "Riprova")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="max-w-xl">
        <h3 className="text-sm font-semibold text-[#020617]">{t("botDetail.widgetIntegration.manualTitle")}</h3>
        <p className="mt-1 text-sm text-[#64748B]">{t("botDetail.widgetIntegration.manualIntro")}</p>
      </div>

      <div className="relative">
        <pre className={`overflow-x-auto rounded-[10px] border border-[#E2E8F0] ${FONDO_CODICE} p-4 pr-28 text-[13px] leading-6`}>
          <code className="font-mono" dangerouslySetInnerHTML={evidenzia(codice())} />
        </pre>
        <button type="button" onClick={() => copia("snippet")} className={`${BOTTONE} ${testoCopia("snippet")} absolute right-2 top-2`}>
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          {t("botDetail.widgetIntegration.copy")}
        </button>
      </div>

      <p className="text-xs text-[#64748B]">{t("botDetail.widgetIntegration.scriptPlacement")}</p>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-[10px] bg-[#F1F5F9] py-0.5 pl-3 pr-0.5 text-xs text-[#475569]">
          {t("botDetail.widgetIntegration.widgetCode")}
          <code className="font-mono text-[#020617]">{embed.widget_code}</code>
          <button
            type="button"
            onClick={() => copia("codice")}
            aria-label={t("botDetail.widgetIntegration.copyWidgetCode")}
            className={`${BOTTONE} h-7 w-7 !px-0 ${testoCopia("codice")}`}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
        {embed.preview_url ? (
          <a href={embed.preview_url} target="_blank" rel="noopener noreferrer" className={SCURO}>
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {t("botDetail.widgetIntegration.preview")}
          </a>
        ) : null}
        {embed.embed_url ? (
          <a href={embed.embed_url} target="_blank" rel="noopener noreferrer" className={`${BOTTONE} ${TESTO_AZIONE}`}>
            <Globe className="h-3.5 w-3.5" aria-hidden="true" />
            {t("botDetail.widgetIntegration.directUrl")}
          </a>
        ) : null}
      </div>
    </div>
  );
}
